import { z } from 'zod';

import { arquivoResposta, instante, page } from './common';
import { ServiceType } from './contratos';
import { defineEndpoint } from './endpoint';

/** Fonte única: `contracts/contratos.ts`. Reexportado para quem já importa daqui. */
export { ServiceType };

/** Tipo do modelo no editor: os termos de filiação de cada produto e o contrato de parceria (não é produto). */
export const TipoModelo = z.enum([...ServiceType.options, 'parceria']);
export type TipoModelo = z.infer<typeof TipoModelo>;

/**
 * `bodyLimit` das rotas que recebem os documentos (o global de 1 MiB recusaria um template válido): até 1 MiB de
 * imagens por documento (`MAX_DOC_IMAGE_BYTES`) × 3 documentos + texto. O teto real das imagens é do domínio.
 */
export const BODY_LIMIT_TEMPLATE = 6 * 1024 * 1024;

/** Documento Tiptap: só a raiz é conferida aqui (como `templateDocsSchema`); o conteúdo, em `findTemplateProblems`. */
const Documento = z.looseObject({ type: z.literal('doc') });

export const TemplateDocs = z.object({ content: Documento, header: Documento, footer: Documento });
export type TemplateDocs = z.infer<typeof TemplateDocs>;

/** `btrim` do Postgres tira só espaços: paridade com a checagem de `criar_versao_template`/`reverter_versao_template`. */
const btrim = (s: string) => s.replace(/^ +| +$/g, '');
const changeNote = z.string().min(10).refine((s) => btrim(s).length >= 10);

/** `isUuid` do legado (`route-helpers.ts`): 8-4-4-4-12 hex, sem exigir versão/variante RFC. */
const ServiceParams = z.object({ serviceType: TipoModelo });
const VersaoParams = z.object({ serviceType: TipoModelo, versionId: z.guid() });

const BASE = '/api/admin/contratos/templates/:serviceType';

/** L1: rotas existentes; corpos iguais aos de `src/app/api/admin/contratos/templates/[serviceType]/**`. */
export const TemplatePublicado = z.object({ versionId: z.guid(), versionNumber: z.number().int(), docs: TemplateDocs });
export type TemplatePublicado = z.infer<typeof TemplatePublicado>;

export const VersaoCriada = z.object({ versionId: z.guid(), versionNumber: z.number().int() });
export type VersaoCriada = z.infer<typeof VersaoCriada>;

/** Item de `listar_versoes_template` (migrations 20260627170100 + 20260629130100), chave a chave. */
export const VersaoResumo = z.object({
  id: z.guid(),
  version_number: z.number().int(),
  created_by_name: z.string().nullable(),
  created_at: instante,
  change_note: z.string().nullable(),
  is_current: z.boolean(),
  origin: z.enum(['edit', 'revert']),
  reverted_from_version_number: z.number().int().nullable(),
});
export type VersaoResumo = z.infer<typeof VersaoResumo>;

/** Campos extras são descartados, como no `z.object` do legado. */
export const CriarVersaoInput = TemplateDocs.extend({ changeNote });
export type CriarVersaoInput = z.infer<typeof CriarVersaoInput>;

export const lerTemplatePublicado = defineEndpoint({
  method: 'GET',
  path: BASE,
  params: ServiceParams,
  output: TemplatePublicado,
});

/** Corpo legado `{ success, data }` com status 201: a rota usa `legado`. */
export const criarVersao = defineEndpoint({
  method: 'POST',
  path: BASE,
  params: ServiceParams,
  body: CriarVersaoInput,
  output: z.object({ success: z.literal(true), data: VersaoCriada }),
});

export const previewTemplate = defineEndpoint({
  method: 'POST',
  path: `${BASE}/preview`,
  params: ServiceParams,
  body: TemplateDocs,
  output: arquivoResposta,
});

export const listarVersoes = defineEndpoint({
  method: 'GET',
  path: `${BASE}/versions`,
  params: ServiceParams,
  query: z.object({ ...page }),
  output: z.array(VersaoResumo),
});

export const lerVersao = defineEndpoint({
  method: 'GET',
  path: `${BASE}/versions/:versionId`,
  params: VersaoParams,
  output: TemplateDocs,
});

export const previewVersao = defineEndpoint({
  method: 'GET',
  path: `${BASE}/versions/:versionId/preview`,
  params: VersaoParams,
  output: arquivoResposta,
});

/** Corpo legado `{ success: true }`, sem `data`: a rota usa `legado`. */
export const publicarVersao = defineEndpoint({
  method: 'POST',
  path: `${BASE}/versions/:versionId/publish`,
  params: VersaoParams,
  output: z.object({ success: z.literal(true) }),
});

export const reverterVersao = defineEndpoint({
  method: 'POST',
  path: `${BASE}/versions/:versionId/revert`,
  params: VersaoParams,
  body: z.object({ changeNote }),
  output: VersaoCriada,
});
